import { useId, useState } from 'react';
import { Link } from 'react-router';

import { formatVnd } from '../../../shared/lib/money.js';
import { toAppError } from '../../../shared/lib/errors.js';
import forms from '../../../shared/styles/forms.module.css';
import lists from '../../../shared/styles/lists.module.css';
import { MoneyInput } from '../../../shared/ui/MoneyInput.js';
import {
  confirmRecurring,
  restoreRecurring,
  skipRecurring,
  undoRecurringConfirm,
  type Obligation,
} from '../data/recurringRepository.js';
import { dueDateLabel } from '../model/recurringLabels.js';
import styles from './DueCard.module.css';

/**
 * "Đến hạn" — what the household owes in the next few days, one tap each (plan step 1.5).
 *
 * Nothing here has happened yet: a template is a forecast until somebody confirms it (B1).
 * That is why the card sits above the month's figures and not inside them — the money is not
 * in the report, it is on the way out.
 *
 * A fixed template confirms in one tap. An estimated one (điện, nước) opens an amount field
 * first and refuses to save without it: the whole point of `amount_mode` is that the app must
 * never invent a bill it has not seen.
 *
 * Two ways out were added after the first real month (plan R1/R2, decisions AE1/AE5): the
 * confirmation that just happened can be undone right here, and an occurrence that should not
 * be posted at all can be skipped. Both ask WHICH correction before writing — "bỏ luôn kỳ
 * này" and "ghi lại sau" leave the schedule in opposite states, and only the person knows
 * which one happened.
 */
export function DueCard({
  obligations,
  timeZone,
  onConfirmed,
}: {
  obligations: readonly Obligation[];
  timeZone: string;
  onConfirmed: (message: string) => void;
}) {
  const headingId = useId();
  const [openId, setOpenId] = useState<string | null>(null);
  const [amount, setAmount] = useState<number | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  // The confirmation that just happened, kept only for this session: the way back for the
  // mistake noticed immediately. The durable way back lives on /dinh-ky (AE5).
  const [lastConfirmed, setLastConfirmed] = useState<{ entryId: string; name: string } | null>(
    null,
  );
  const [undoOpen, setUndoOpen] = useState(false);
  const [skipOpenId, setSkipOpenId] = useState<string | null>(null);
  // The skip that just happened, kept for this session: the way back when "Bỏ qua" was the
  // wrong call (AJ1 — the salary occurrence skipped because the money sat in the opening
  // balance, needed again once that balance was corrected).
  const [lastSkipped, setLastSkipped] = useState<{
    templateId: string;
    dueDate: string;
    name: string;
  } | null>(null);

  // Nothing due AND nothing just confirmed: the card has nothing to say. But when the
  // confirmation that just happened emptied the list — the ordinary case for a household
  // with one template — the card must stay for the way back (AE5); returning null here
  // took the "Hoàn tác" button away in the very moment it was offered (CI 2026-09-15).
  if (obligations.length === 0 && lastConfirmed === null && lastSkipped === null) return null;

  /** Identity of an occurrence: the same template is due again next month. */
  const keyOf = (item: Obligation) => `${item.templateId}:${item.dueDate}`;

  async function confirm(item: Obligation, withAmount?: number) {
    setBusyId(keyOf(item));
    setMessage(null);
    try {
      const entryId = await confirmRecurring({
        templateId: item.templateId,
        dueDate: item.dueDate,
        ...(withAmount === undefined ? {} : { amount: withAmount }),
      });
      setOpenId(null);
      setAmount(null);
      setLastConfirmed({ entryId, name: item.name });
      setUndoOpen(false);
      onConfirmed(`Đã ghi "${item.name}".`);
    } catch (error) {
      setMessage(toAppError(error).message);
    } finally {
      setBusyId(null);
    }
  }

  async function undo(mode: 'skip' | 'redo') {
    if (lastConfirmed === null) return;
    setBusyId(lastConfirmed.entryId);
    setMessage(null);
    try {
      await undoRecurringConfirm({
        entryId: lastConfirmed.entryId,
        mode,
        reason: 'Hoàn tác trên Đến hạn',
      });
      const name = lastConfirmed.name;
      setLastConfirmed(null);
      setUndoOpen(false);
      onConfirmed(
        mode === 'skip'
          ? `Đã hoàn tác "${name}" — kỳ này coi như đã xong.`
          : `Đã hoàn tác "${name}" — kỳ này hiện lại để ghi đúng.`,
      );
    } catch (error) {
      setMessage(toAppError(error).message);
    } finally {
      setBusyId(null);
    }
  }

  async function restore() {
    if (lastSkipped === null) return;
    const key = `${lastSkipped.templateId}:${lastSkipped.dueDate}`;
    setBusyId(key);
    setMessage(null);
    try {
      await restoreRecurring({
        templateId: lastSkipped.templateId,
        dueDate: lastSkipped.dueDate,
        reason: 'Khôi phục trên Đến hạn',
      });
      const name = lastSkipped.name;
      setLastSkipped(null);
      onConfirmed(`Đã khôi phục kỳ này của "${name}" — hiện lại ở Đến hạn.`);
    } catch (error) {
      setMessage(toAppError(error).message);
    } finally {
      setBusyId(null);
    }
  }

  async function skip(item: Obligation) {
    setBusyId(keyOf(item));
    setMessage(null);
    try {
      await skipRecurring({
        templateId: item.templateId,
        dueDate: item.dueDate,
        reason: 'Bỏ qua trên Đến hạn',
      });
      setSkipOpenId(null);
      setLastSkipped({ templateId: item.templateId, dueDate: item.dueDate, name: item.name });
      onConfirmed(`Đã bỏ qua kỳ này của "${item.name}".`);
    } catch (error) {
      setMessage(toAppError(error).message);
    } finally {
      setBusyId(null);
    }
  }

  return (
    <section className={lists.card} aria-labelledby={headingId}>
      <div className={styles.heading}>
        <h2 className={lists.cardTitle} id={headingId}>
          Đến hạn
        </h2>
        <Link className={styles.manageLink} to="/dinh-ky">
          Quản lý
        </Link>
      </div>

      {message ? (
        <p className={forms.error} role="alert">
          {message}
        </p>
      ) : null}

      {lastConfirmed !== null ? (
        <div>
          <p className={styles.undoNotice}>
            {/* The page above already says what was recorded; repeating it here would put
                the same sentence on screen twice. This line only offers the way back. */}
            <span className={lists.rowMeta}>Ghi nhầm &quot;{lastConfirmed.name}&quot;?</span>
            <button
              type="button"
              className={styles.secondaryAction}
              disabled={busyId === lastConfirmed.entryId}
              onClick={() => setUndoOpen(!undoOpen)}
            >
              Hoàn tác
            </button>
          </p>
          {undoOpen ? (
            <CorrectionChoice
              question="Hoàn tác kiểu nào?"
              disabled={busyId === lastConfirmed.entryId}
              options={[
                { label: 'Bỏ luôn kỳ này', onSelect: () => void undo('skip') },
                { label: 'Ghi lại sau', onSelect: () => void undo('redo') },
              ]}
              onCancel={() => setUndoOpen(false)}
            />
          ) : null}
        </div>
      ) : null}

      {lastSkipped !== null ? (
        <p className={styles.undoNotice}>
          <span className={lists.rowMeta}>Bỏ qua nhầm &quot;{lastSkipped.name}&quot;?</span>
          <button
            type="button"
            className={styles.secondaryAction}
            disabled={busyId === `${lastSkipped.templateId}:${lastSkipped.dueDate}`}
            onClick={() => void restore()}
          >
            Khôi phục
          </button>
        </p>
      ) : null}

      <ul className={lists.list}>
        {obligations.map((item) => {
          const key = keyOf(item);
          const isOpen = openId === key;
          const isBusy = busyId === key;

          return (
            <li key={key}>
              <div className={styles.row}>
                <span className={lists.rowText}>
                  <span className={lists.rowName}>{item.name}</span>
                  <span className={lists.rowMeta}>
                    {dueDateLabel(item.dueDate, timeZone)}
                    {item.amountMode === 'estimated' ? ' · số tiền thay đổi' : ''}
                  </span>
                </span>

                <span className={styles.right}>
                  {item.amount === null ? null : (
                    <span className={lists.amount}>{formatVnd(item.amount)}</span>
                  )}
                  <button
                    type="button"
                    className={styles.secondaryAction}
                    disabled={isBusy}
                    aria-label={`Bỏ qua ${item.name}`}
                    onClick={() => {
                      setSkipOpenId(skipOpenId === key ? null : key);
                      setMessage(null);
                    }}
                  >
                    Bỏ qua
                  </button>
                  <button
                    type="button"
                    className={styles.confirm}
                    disabled={isBusy}
                    onClick={() => {
                      // A fixed amount is already known, so confirming is the whole gesture.
                      if (item.amountMode === 'fixed') void confirm(item);
                      else {
                        setOpenId(isOpen ? null : key);
                        setAmount(null);
                        setMessage(null);
                      }
                    }}
                  >
                    {isBusy ? 'Đang ghi…' : 'Xác nhận'}
                  </button>
                </span>
              </div>

              {isOpen ? (
                <AmountPrompt
                  value={amount}
                  onChange={setAmount}
                  disabled={isBusy}
                  onCancel={() => setOpenId(null)}
                  onSubmit={() => {
                    if (amount !== null && amount > 0) void confirm(item, amount);
                  }}
                />
              ) : null}

              {skipOpenId === key ? (
                <CorrectionChoice
                  question="Bỏ qua thế nào?"
                  disabled={isBusy}
                  options={[
                    { label: 'Chỉ kỳ này', onSelect: () => void skip(item) },
                    {
                      label: 'Dừng khoản định kỳ này',
                      href: '/dinh-ky',
                    },
                  ]}
                  onCancel={() => setSkipOpenId(null)}
                />
              ) : null}

              {item.isOverdue ? <p className={styles.overdue}>Đã quá hạn</p> : null}
            </li>
          );
        })}
      </ul>
    </section>
  );
}

/** The bill arrived: type what it actually says. */
function AmountPrompt({
  value,
  onChange,
  disabled,
  onCancel,
  onSubmit,
}: {
  value: number | null;
  onChange: (amount: number | null) => void;
  disabled: boolean;
  onCancel: () => void;
  onSubmit: () => void;
}) {
  const inputId = useId();

  return (
    <div className={styles.prompt}>
      <div className={forms.field}>
        <label htmlFor={inputId}>Số tiền thực tế</label>
        <MoneyInput id={inputId} value={value} onChange={onChange} autoFocus />
      </div>
      <div className={styles.promptActions}>
        <button type="button" className={forms.secondary} onClick={onCancel}>
          Hủy
        </button>
        <button
          type="button"
          className={forms.primary}
          disabled={disabled || value === null || value <= 0}
          onClick={onSubmit}
        >
          Ghi khoản này
        </button>
      </div>
    </div>
  );
}

/**
 * The two-tap box in front of every correction.
 *
 * One button would be simpler and wrong: "bỏ luôn kỳ này" and "ghi lại sau" leave the schedule
 * in opposite states, and the app cannot tell from the outside which mistake was made.
 */
function CorrectionChoice({
  question,
  options,
  disabled,
  onCancel,
}: {
  question: string;
  options: ReadonlyArray<{ label: string; onSelect?: () => void; href?: string }>;
  disabled: boolean;
  onCancel: () => void;
}) {
  return (
    <div className={styles.choice}>
      <p className={styles.choiceTitle}>{question}</p>
      <div className={styles.choiceActions}>
        {options.map((option) =>
          option.href === undefined ? (
            <button
              key={option.label}
              type="button"
              className={forms.secondary}
              disabled={disabled}
              onClick={option.onSelect}
            >
              {option.label}
            </button>
          ) : (
            <Link key={option.label} className={forms.secondary} to={option.href}>
              {option.label}
            </Link>
          ),
        )}
        <button type="button" className={forms.secondary} onClick={onCancel}>
          Hủy
        </button>
      </div>
    </div>
  );
}
